import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Button, AuthLocaleSelect, LoginView, toast, type SocialProviderId } from "@app/ui";
import { authClient } from "@/lib/AuthClient";
import { RateLimitError } from "@/lib/rateLimitNotice";
import { getDesktopBridge } from "@/lib/Desktop";
import { useLocaleToggle } from "@/hooks/UseLocaleToggle";
import { setLastAuthMethod, useLastAuthMethod } from "@/lib/LastAuthMethod";
import { AUTH_FORGOT_PATH, AUTH_LINK_PATH, AUTH_LOGIN_PATH, AUTH_REGISTER_PATH, AUTH_VERIFY_PATH, navigate, usePathname } from "@/lib/Pathname";
import { detectTimezone } from "@/lib/Timezone";
import { useLoginService } from "@/queries/login";
import { useAuthProvidersQuery } from "@/queries/authProviders";
import { authServerErrorDescriptor } from "shared/dto/auth/messages";
import { isCapacitorNative } from "shared/features/platform";
import { openNativeSocialLogin } from "@/lib/NativeAuth";

/**
 * Electron 셸 안에서는 이메일/비밀번호 폼 대신 이 버튼만 보여준다. 클릭하면
 * 시스템 기본 브라우저에서 `/auth/link` 를 열어 그쪽에서 로그인하게 하고,
 * 로그인이 끝나면 pages/desktop-handoff가 withtask:// 딥링크로 앱에 세션을
 * 되돌려준다(App.tsx의 onDeepLink 리스너 참고).
 */
function openBrowserLogin(): void {
  getDesktopBridge()?.openExternal(`${window.location.origin}${AUTH_LINK_PATH}`);
}

function DesktopBrowserLogin() {
  const { locale, onLocaleChange } = useLocaleToggle();
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background p-6 pt-[calc(1.5rem+var(--app-titlebar-height))]">
      {/* 타이틀바와 겹치지 않도록 top 에 타이틀바 높이를 더한다. */}
      <AuthLocaleSelect
        locale={locale}
        onLocaleChange={onLocaleChange}
        className="top-[calc(1.5rem+var(--app-titlebar-height))]"
      />
      <div className="flex w-full max-w-xs flex-col items-center gap-4 text-center">
        <h1 className="text-base font-semibold">
          <Trans>로그인</Trans>
        </h1>
        <p className="text-sm text-muted-foreground">
          <Trans>브라우저에서 로그인을 마치면 자동으로 돌아와요.</Trans>
        </p>
        <Button className="mt-2 w-full" onClick={openBrowserLogin}>
          <Trans>브라우저에서 로그인</Trans>
        </Button>
      </div>
    </div>
  );
}

/**
 * 인증 코드 입력 화면으로 이동한다. 이 시점엔 세션이 없어서(가입은 로그인시키지 않는다)
 * 대상 이메일을 서버에서 읽어올 수 없으므로 쿼리로 넘긴다 — 새로고침에도 살아남는다.
 */
function goVerify(email: string): void {
  navigate(`${AUTH_VERIFY_PATH}?email=${encodeURIComponent(email)}`);
}

function EmailPasswordLogin() {
  const { t, i18n } = useLingui();
  const { locale, onLocaleChange } = useLocaleToggle();
  const signUpMutation = useLoginService();
  const pathname = usePathname();
  const mode = pathname === AUTH_REGISTER_PATH ? "register" : "login";
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 서버가 켠 소셜 프로바이더(게이팅) + 직전 로그인 방법("최근 사용" 강조).
  const { data: authProviders } = useAuthProvidersQuery();
  const lastAuthMethod = useLastAuthMethod();

  const handleSubmit = async ({ email, password }: { email: string; password: string }) => {
    setIsSubmitting(true);
    try {
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) {
        // 가입만 하고 코드 인증 전에 이탈한 계정의 복구 경로. better-auth 가 이 응답과
        // 함께 인증 코드를 다시 보내주므로 바로 입력 화면으로 넘긴다.
        if (signInError.code === "EMAIL_NOT_VERIFIED") {
          goVerify(email);
          return;
        }
        // rate limit(429)은 안내 토스트를 fetch 래퍼가 이미 띄웠으므로 겹쳐 띄우지 않는다.
        // status 는 better-fetch 가 런타임에 실어 주지만 클라이언트 에러 타입엔 없어 좁혀 캐스트한다.
        if ((signInError as { status?: number }).status === 429) return;
        // better-auth 는 영어 message 를 채워 보내므로 그대로 쓰면 안 된다 — 안정적인 code 로
        // 번역하고, 매핑에 없는 코드는 영어 노출 없이 일반 한국어 폴백을 쓴다.
        const descriptor = authServerErrorDescriptor(signInError.code);
        toast.error(descriptor ? i18n._(descriptor) : t`로그인에 실패했습니다.`);
      } else {
        // 로그인 성공 — 다음 방문에 이 방법을 "최근 사용"으로 끌어올린다.
        setLastAuthMethod("email");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /** 클릭 시 better-auth 가 브라우저를 프로바이더 인가 화면으로 리다이렉트한다.
   * 인증이 끝나면 "/" 로 돌아오고, App.tsx 의 세션 게이트가 온보딩/대시보드로 넘긴다. */
  const handleSocialLogin = async (provider: SocialProviderId) => {
    // 소셜은 성공 시 페이지가 리다이렉트로 떠나 이후 코드가 돌지 않는다 — 그래서
    // 리다이렉트 "직전"에 최근 사용으로 기록한다. 인증에 실패해 되돌아오면 다음 성공이
    // 덮어쓰므로, 실패한 시도가 "최근 사용"으로 남는 건 무해한 근사치다.
    setLastAuthMethod(provider);
    // 네이티브(iOS/Android) 웹뷰 안에서 OAuth 를 돌리면 단명 state 쿠키 유실로
    // state_mismatch 가 나고 구글도 임베디드 웹뷰를 차단한다 — 시스템 브라우저에서
    // 로그인 후 딥링크로 세션을 넘겨받는다(lib/NativeAuth + components/PlatformBridge).
    if (isCapacitorNative()) {
      try {
        await openNativeSocialLogin(provider);
      } catch {
        toast.error(t`로그인에 실패했습니다.`);
      }
      return;
    }
    const { error } = await authClient.signIn.social({ provider, callbackURL: "/" });
    if (error) toast.error(t`로그인에 실패했습니다.`);
  };

  const handleSignUp = async ({
    email,
    nickname,
    password,
  }: {
    email: string;
    nickname: string;
    password: string;
  }) => {
    setIsSubmitting(true);
    try {
      await signUpMutation.mutateAsync({ email, nickname, password, timezone: detectTimezone() });
    } catch (e) {
      // rate limit(429)은 안내 토스트를 fetch 래퍼가 이미 띄웠으므로 겹쳐 띄우지 않는다.
      if (e instanceof RateLimitError) return;
      toast.error(e instanceof Error ? e.message : t`가입에 실패했습니다.`);
      return;
    } finally {
      setIsSubmitting(false);
    }
    // 가입은 세션을 만들지 않는다 — 메일로 간 인증 코드를 넣어야 로그인된다.
    goVerify(email);
  };

  return (
    <LoginView
      mode={mode}
      onSubmit={handleSubmit}
      onSignUp={handleSignUp}
      onSwitchMode={() => navigate(mode === "login" ? AUTH_REGISTER_PATH : AUTH_LOGIN_PATH)}
      onForgotPassword={() => navigate(AUTH_FORGOT_PATH)}
      socialProviders={authProviders?.providers}
      lastAuthMethod={lastAuthMethod}
      onSocialLogin={handleSocialLogin}
      locale={locale}
      onLocaleChange={onLocaleChange}
      isSubmitting={isSubmitting}
    />
  );
}

export function LoginPage() {
  return getDesktopBridge() ? <DesktopBrowserLogin /> : <EmailPasswordLogin />;
}
